// Общие схемы курсорной пагинации, разделяемые между модулями.
import { z } from "zod";

/**
 * Курсор пагинации.
 * Обычно — opaque-строка (ISO datetime либо base64 от UUID v7 + createdAt).
 * Клиент НЕ интерпретирует значение, только эхо-возвращает на сервер.
 */
export const cursorSchema = z.string().min(1).nullable();

/**
 * Стандартный input для cursor-based списков.
 * limit ∈ [1, maxLimit]; default 20; cursor от предыдущей страницы.
 */
export function cursorPaginationInput(opts: { defaultLimit?: number; maxLimit?: number } = {}) {
	const defaultLimit = opts.defaultLimit ?? 20;
	const maxLimit = opts.maxLimit ?? 50;
	return z.object({
		cursor: cursorSchema.optional().default(null),
		limit: z.number().int().min(1).max(maxLimit).default(defaultLimit),
	});
}

/**
 * Generic-фабрика для cursor-pagination output.
 * Возвращает Zod-схему вида `{ items: Item[]; nextCursor: string | null }`.
 */
export function cursorPaginationOutput<TItem extends z.ZodTypeAny>(itemSchema: TItem) {
	return z.object({
		items: z.array(itemSchema),
		nextCursor: cursorSchema,
	});
}

export type CursorPaginationInput = z.infer<ReturnType<typeof cursorPaginationInput>>;

/**
 * Offset-based пагинация для UI с jump-to-page (glossary alphabet, admin tables).
 * Использовать когда totalCount важен и стабильность курсора неважна.
 */
export const offsetPaginationInput = z.object({
	offset: z.number().int().min(0).default(0),
	limit: z.number().int().min(1).max(200).default(50),
});
export type OffsetPaginationInput = z.infer<typeof offsetPaginationInput>;

/**
 * Generic-фабрика для offset-pagination output.
 * Возвращает Zod-схему вида `{ items: Item[]; totalCount: number }`.
 */
export function offsetPaginationOutput<TItem extends z.ZodTypeAny>(itemSchema: TItem) {
	return z.object({
		items: z.array(itemSchema),
		totalCount: z.number().int().nonnegative(),
	});
}
